import React from 'react';
import { useParams } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { formatTimestamp } from 'utils/formatTimestamp';
import { formatCurrency } from 'utils/formatters/formatCurrency';
import { convertCentsToDollars } from 'utils/convertMoney';
import { PAYMENT_PROCESSOR } from 'utils/constants';
import { usePaymentDiscrepancyLog } from 'api/queries/usePaymentDiscrepancyLog';
import styles from './PaymentDiscrepancyLogDetail.module.scss';

const SOURCE_LABELS: Record<string, string> = {
  cron: 'Reconciliation Job',
  stripe_webhook: 'Stripe Webhook',
};

const TYPE_LABELS: Record<string, string> = {
  balance: 'Balance',
  tip: 'Tip',
};

const StripeLink = ({ href, id }: { href: string; id: string }) => (
  <a className={styles.stripeLink} href={href} target="_blank" rel="noopener noreferrer">
    {id}
  </a>
);

const PaymentDiscrepancyLogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data: log, isLoading } = usePaymentDiscrepancyLog(id);

  if (isLoading) return <CentsLoader />;
  if (!log) return null;

  return (
    <div className="page-container">
      <h1 className="payments-header">Payment Discrepancy #{log.id}</h1>

      <div className={styles.summaryCard}>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Created</span>
          <span>{formatTimestamp(log.createdAt)}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Source</span>
          <span>{SOURCE_LABELS[log.source] ?? log.source}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Type</span>
          <span>
            {log.discrepancyType ? (
              <Chip
                size="small"
                color="default"
                label={TYPE_LABELS[log.discrepancyType] ?? log.discrepancyType}
              />
            ) : (
              <Chip size="small" color="default" label="Unknown" />
            )}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Status</span>
          <span>
            {log.resolvedAt ? (
              <Chip size="small" color="success" label="Resolved" />
            ) : (
              <Chip size="small" color="warning" label="Open" />
            )}
          </span>
        </div>
        {log.resolvedAt && (
          <div className={styles.summaryRow}>
            <span className={styles.label}>Resolved At</span>
            <span>{formatTimestamp(log.resolvedAt)}</span>
          </div>
        )}
        <div className={styles.summaryRow}>
          <span className={styles.label}>Order</span>
          <a href={`/service-orders/${log.serviceOrderId}`}>{log.serviceOrderId}</a>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Amount Expected</span>
          <span>{formatCurrency(convertCentsToDollars(log.netOrderTotalInCents))}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Amount Paid</span>
          <span>{formatCurrency(convertCentsToDollars(log.paidAmountInCents))}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.label}>Balance Due</span>
          <span>{formatCurrency(convertCentsToDollars(log.balanceDueInCents))}</span>
        </div>
      </div>

      {log.discrepancyType === 'tip' && log.details && (
        <div className={styles.summaryCard}>
          <h2 className={styles.sectionHeader}>Tip Details</h2>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Tip Option</span>
            <span>{log.details.tipOption ?? '--'}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Expected Tip</span>
            <span>
              {formatCurrency(convertCentsToDollars(log.details.expectedTipAmountInCents))}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Stored Tip</span>
            <span>
              {formatCurrency(convertCentsToDollars(log.details.storedTipAmountInCents))}
            </span>
          </div>
        </div>
      )}

      <h2 className={styles.sectionHeader}>Payments &amp; Refunds</h2>
      {!log.paymentsAndRefunds.length && <p>No payments found for this order.</p>}
      {log.paymentsAndRefunds.map((payment) => (
        <div key={payment.id} className={styles.paymentCard}>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Payment ID</span>
            <span>{payment.id}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Status</span>
            <span>{payment.status}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Processor</span>
            <span>{payment.paymentProcessor}</span>
          </div>
          {payment.paymentProcessor === PAYMENT_PROCESSOR.STRIPE && payment.paymentToken && (
            <div className={styles.summaryRow}>
              <span className={styles.label}>Stripe Payment</span>
              <StripeLink
                href={`https://dashboard.stripe.com/payments/${payment.paymentToken}`}
                id={payment.paymentToken}
              />
            </div>
          )}
          <div className={styles.summaryRow}>
            <span className={styles.label}>Total Amount</span>
            <span>{formatCurrency(payment.totalAmount)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Amount Applied to This Order</span>
            <span>
              {payment.orderPaymentAmount != null ? formatCurrency(payment.orderPaymentAmount) : '--'}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Total Refunded</span>
            <span>{formatCurrency(payment.totalRefunded)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.label}>Created</span>
            <span>{formatTimestamp(payment.createdAt)}</span>
          </div>

          {!!payment.refunds.length && (
            <table className={styles.refundsTable}>
              <thead>
                <tr>
                  <th>Refund ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Created</th>
                  <th>Stripe Refund</th>
                </tr>
              </thead>
              <tbody>
                {payment.refunds.map((refund) => (
                  <tr key={refund.id}>
                    <td>{refund.id}</td>
                    <td>{formatCurrency(convertCentsToDollars(refund.refundAmountInCents))}</td>
                    <td>{refund.status}</td>
                    <td>{refund.reason}</td>
                    <td>{formatTimestamp(refund.createdAt)}</td>
                    <td>
                      {refund.refundProvider === PAYMENT_PROCESSOR.STRIPE &&
                      refund.thirdPartyRefundId ? (
                        <StripeLink
                          href={`https://dashboard.stripe.com/refunds/${refund.thirdPartyRefundId}`}
                          id={refund.thirdPartyRefundId}
                        />
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default PaymentDiscrepancyLogDetail;
